import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import {
  Badge,
  Card,
  EmptyState,
  Field,
  Loading,
  PageHeader,
  Select,
  Table,
  Tabs,
  Td,
} from '../components/ui/primitives';
import { EngagementPanel } from '../components/analytics/EngagementPanel';
import { BotPanel, ClassificationBadge } from '../components/analytics/BotPanel';
import { ProviderTable } from '../components/deliverability/ProviderTable';
import { dateTime } from '../lib/format';
import type {
  BotActivity,
  CampaignRecord,
  ClientRecord,
  EngagementReport,
  ProviderRow,
  TrackingEventRow,
} from '../lib/types';

export function AnalyticsPage() {
  const [tab, setTab] = useState('engagement');
  const [clientId, setClientId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [days, setDays] = useState(30);
  const [classification, setClassification] = useState('');

  const filter = qs({ clientId, campaignId, days });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ clients: ClientRecord[] }>('/api/clients'),
  });
  const { data: campaigns } = useQuery({
    queryKey: ['campaigns', clientId],
    queryFn: () => api.get<{ campaigns: CampaignRecord[] }>('/api/campaigns' + qs({ clientId })),
  });

  const { data: engagement } = useQuery({
    queryKey: ['engagement', filter],
    queryFn: () => api.get<EngagementReport>('/api/analytics/engagement' + filter),
  });
  const { data: timeline } = useQuery({
    queryKey: ['timeline', filter],
    queryFn: () => api.get<{ series: never[] }>('/api/analytics/timeline' + filter),
  });
  const { data: bots } = useQuery({
    queryKey: ['bots', filter],
    queryFn: () => api.get<BotActivity>('/api/analytics/bots' + filter),
  });
  const { data: providers } = useQuery({
    queryKey: ['providers', filter],
    queryFn: () => api.get<{ providers: ProviderRow[] }>('/api/analytics/providers' + filter),
  });
  const { data: events } = useQuery({
    queryKey: ['events', clientId, campaignId, classification],
    queryFn: () =>
      api.get<{ events: TrackingEventRow[] }>(
        '/api/analytics/events' + qs({ clientId, campaignId, classification, limit: 150 })
      ),
    enabled: tab === 'events',
  });

  return (
    <>
      <PageHeader
        title="Engagement"
        description="Every number here exists in two versions: filtered (human only) and unfiltered. The event explorer shows the raw hit behind each one, with the rules that classified it."
      />

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <Field label="Client">
          <Select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setCampaignId('');
            }}
          >
            <option value="">All clients</option>
            {(clients?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Campaign">
          <Select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">All campaigns</option>
            {(campaigns?.campaigns ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Period">
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </Select>
        </Field>
      </div>

      <Tabs
        tabs={[
          { id: 'engagement', label: 'Engagement' },
          { id: 'bots', label: 'Automated activity' },
          { id: 'providers', label: 'By provider' },
          { id: 'events', label: 'Event explorer' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'engagement' &&
        (engagement ? <EngagementPanel report={engagement} timeline={timeline?.series} /> : <Loading />)}

      {tab === 'bots' && (bots ? <BotPanel data={bots} /> : <Loading />)}

      {tab === 'providers' && (
        <Card>{providers ? <ProviderTable rows={providers.providers} /> : <Loading />}</Card>
      )}

      {tab === 'events' && (
        <Card
          title="Raw engagement events"
          subtitle="The audit trail behind every filtered figure. Each row shows the classification, the score and the exact rules that fired."
          actions={
            <Select value={classification} onChange={(e) => setClassification(e.target.value)}>
              <option value="">All classifications</option>
              <option value="human">Human</option>
              <option value="machine_open">Machine open</option>
              <option value="suspected_bot">Suspected bot</option>
              <option value="bot">Bot</option>
            </Select>
          }
        >
          {!events ? (
            <Loading />
          ) : !events.events.length ? (
            <EmptyState title="No events recorded" />
          ) : (
            <Table
              head={['When', 'Type', 'Recipient', 'Classification', 'Score', 'Source', 'Rules that fired']}
            >
              {events.events.map((e) => (
                <tr key={e.id}>
                  <Td className="whitespace-nowrap text-xs">
                    {dateTime(e.occurredAt)}
                    {e.secondsSinceDelivery !== null && (
                      <div className="text-[11px] text-ink-400">
                        +{e.secondsSinceDelivery.toFixed(1)}s after delivery
                      </div>
                    )}
                  </Td>
                  <Td>
                    <Badge>{e.type}</Badge>
                  </Td>
                  <Td className="font-mono text-xs">{e.email}</Td>
                  <Td>
                    <ClassificationBadge classification={e.classification} />
                    {e.scannerName && (
                      <div className="mt-1 text-[11px] text-ink-500">{e.scannerName}</div>
                    )}
                  </Td>
                  <Td className="tabular-nums">{e.botScore.toFixed(0)}</Td>
                  <Td className="max-w-[16rem]">
                    <div className="font-mono text-[11px] text-ink-600">{e.ip ?? '--'}</div>
                    <div className="truncate text-[11px] text-ink-400" title={e.userAgent ?? ''}>
                      {e.ipOrg ?? e.uaFamily ?? e.userAgent ?? ''}
                    </div>
                  </Td>
                  <Td className="max-w-md">
                    {e.ruleHits.length === 0 ? (
                      <span className="text-xs text-ink-400">none</span>
                    ) : (
                      <ul className="space-y-0.5 text-[11px] text-ink-600">
                        {e.ruleHits.map((hit, i) => (
                          <li key={i}>
                            {hit.label}
                            {hit.detail && <span className="text-ink-400"> - {hit.detail}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}
    </>
  );
}
